import React from "react";
import LegalLayout from "./LegalLayout";

const UPDATED = "3 July 2026";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated={UPDATED}>
      <Section title="1. Who we are">
        <p>
          These Terms of Service ("Terms") govern your use of the Leadmato website (leadmato.com,
          www.leadmato.com) and the Leadmato SaaS application ("Service") operated by the
          Leadmato team ("we", "us", "our"). By creating an account or making a payment, you agree
          to these Terms and our{" "}
          <a className="text-lm-primary underline underline-offset-4" href="/privacy">
            Privacy Policy
          </a>
          .
        </p>
      </Section>

      <Section title="2. Eligibility">
        <p>
          You must be at least 18 years old and legally capable of entering into a binding
          contract in your jurisdiction to use Leadmato. You represent that all information you
          provide is accurate and that you will keep it up to date.
        </p>
      </Section>

      <Section title="3. Your account & workspace">
        <ul>
          <li>Each account is created via Google sign-in and is bound to a single email.</li>
          <li>
            A workspace is auto-created on first sign-in. You are responsible for keeping your
            account credentials secure and for all activity in your workspace.
          </li>
          <li>
            Sharing a paid workspace across multiple business entities is not permitted without
            written consent from us.
          </li>
        </ul>
      </Section>

      <Section title="4. Plan, payments & subscription">
        <ul>
          <li>
            <strong>Free trial.</strong> A one-time trial per workspace — 1 brand import and 1
            Find-Customers scan (view-only). This is a lifetime one-time allowance, not a recurring
            free plan.
          </li>
          <li>
            <strong>Starter plan.</strong> Our single paid plan is a monthly subscription priced at
            INR 1,999 per month (inclusive of GST) in India, or USD 19.99 per month globally. It
            includes 15 scans per month, up to 15 keywords per scan, and 1 brand. Prices are shown
            at checkout and include applicable taxes where required.
          </li>
          <li>
            Payments are processed by third-party providers — USD via Stripe and INR via Razorpay.
            We do not store card details.
          </li>
          <li>
            The Starter subscription renews monthly until you cancel. You may cancel at any time; on
            cancellation your access continues until the end of the current billing period. We
            reserve the right to sunset the Service with a minimum of 90 days' notice, in which case
            affected subscribers will receive a pro-rated credit or refund at our discretion (see
            Refund Policy).
          </li>
          <li>
            Third-party data sources (Reddit, X, LinkedIn, YouTube, etc.) may change or restrict
            their access at any time; while we will adapt, some features may become limited or
            unavailable due to external factors outside our control.
          </li>
        </ul>
      </Section>

      <Section title="5. Fair-use policy">
        <p>
          To keep the Service reliable for everyone, we apply automated fair-use limits — for
          example, monthly scan caps, IP-based rate limits, and content-safety filters. Abuse
          (scraping our results in bulk, sharing an account across many operators, running
          automated bots against our endpoints) may result in temporary rate limits or account
          suspension.
        </p>
      </Section>

      <Section title="6. Acceptable use">
        <ul>
          <li>
            Do not use Leadmato to send spam, harass individuals, or violate any law, including
            data-protection and anti-spam laws in your jurisdiction and the jurisdictions of
            the people you contact.
          </li>
          <li>
            You are responsible for how you use the leads and drafts Leadmato surfaces — including
            complying with each platform's own terms (Reddit, X, LinkedIn, YouTube, etc.) when
            you engage with users there.
          </li>
          <li>
            Do not attempt to reverse-engineer, decompile, or bypass the security of the Service.
          </li>
        </ul>
      </Section>

      <Section title="7. Content & intellectual property">
        <p>
          The Leadmato product, brand, code and materials are owned by us. You retain ownership
          of the brand-foundation content and notes you input. By using the Service, you grant
          us a non-exclusive licence to process your inputs solely to provide the Service to
          you.
        </p>
      </Section>

      <Section title="8. Third-party services">
        <p>
          Leadmato uses third-party services including Google (auth), Emergent LLM providers
          (Anthropic, OpenAI, Gemini), Jina, Razorpay, Stripe, and public social APIs. Your use
          of the Service is subject to their respective terms.
        </p>
      </Section>

      <Section title="9. Warranties & liability">
        <p>
          The Service is provided "as is" without warranty of any kind. To the maximum extent
          permitted by law, our aggregate liability arising out of these Terms is limited to the
          amount you paid us in the 12 months preceding the claim, or INR 1,999 (whichever is
          higher).
        </p>
      </Section>

      <Section title="10. Termination">
        <p>
          You may delete your account at any time by contacting support. We may suspend or
          terminate your account for material breach of these Terms, subject to notice where
          practicable.
        </p>
      </Section>

      <Section title="11. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be communicated by
          in-app notice or email. Continued use of the Service after the effective date
          constitutes acceptance.
        </p>
      </Section>

      <Section title="12. Governing law & jurisdiction">
        <p>
          These Terms are governed by the laws of India. The courts at Kolkata, West Bengal,
          shall have exclusive jurisdiction over any disputes.
        </p>
      </Section>

      <Section title="13. Contact">
        <p>
          Questions about these Terms?{" "}
          <a className="text-lm-primary underline underline-offset-4" href="/contact">
            Contact us
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-display font-semibold text-xl md:text-2xl tracking-tight text-lm-text mb-3">
        {title}
      </h2>
      <div className="text-lm-text2 space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:text-lm-primary [&_a]:underline [&_a]:underline-offset-4">
        {children}
      </div>
    </section>
  );
}
